import axios from 'axios'
import store from '@/store/index'
import GUID from '@/js/guid'
import router from '@/router/index.js'

export default {
  useOfflineMode: false,
  isPostingData: false,
  apiEndpoints: {
    checkIn: process.env.APIENDPOINT_CHECK_IN,
    clive: process.env.APIENDPOINT_CLIVE
  },

  /**
   * Main method to GET data from the server
   *
   * @param {*} url
   * @param {*} value
   * @param {*} progressCallback
   * @param {*} offlineCallbackFunction
   */
  get (endpoint, url, value, progressCallback, offlineCallbackFunction) {
    // Local copy of the store
    const scope = this
    var localStoreObj = store
    var localUseOfflineMode = this.useOfflineMode

    return new Promise(function (resolve, reject) {
      // Build the config
      const config = {
        onUploadProgress: progressEvent => {
          if (progressCallback != null) {
            progressCallback(progressEvent)
          }
        }
      }
      // Build the URL
      const fullpath = scope.apiEndpoints[endpoint] + url
      // Try to make the request to the server
      axios.get(fullpath, { params: value, config: config })
        .then(function (res) {
          // If all is well return the data from the server
          resolve(res.data)
        })
        .catch(function (err) {
          if (err != null && err.message === 'Network Error' && localUseOfflineMode === true) {
            // If an offline callback is specified use it
            if (offlineCallbackFunction != null) {
              resolve(localStoreObj.dispatch(offlineCallbackFunction, { url: url, value: value, state: localStoreObj.state }))
            } else {
              resolve()
            }
          } else if (err.response) {
            if (err.response.status === 401) {
              reject(new Error(err.response.statusText + ' - You do not have access to view this data'))
            } else {
              reject(err)
            }
          } else {
            reject(err)
          }
        })
    })
  },

  /**
   * Main method to POST data from the server
   *
   * @param {*} url
   * @param {*} value
   * @param {*} progressCallback
   * @param {*} offlineCallbackFunction
   */
  post (endpoint, url, value, progressCallback, offlineCallbackFunction) {
    // Local copies
    var scope = this
    var localStoreObj = store
    var localUseOfflineMode = this.useOfflineMode

    // If nothing is waiting to be sent this can be initiated immediately
    if (!localUseOfflineMode || localStoreObj.getters['offline/isOfflineQueueEmpty']) {
      // Try to make the request to the server
      return new Promise(function (resolve, reject) {
        // Build a config based on the parameters
        const config = {
          onUploadProgress: progressEvent => {
            if (progressCallback != null) {
              progressCallback(progressEvent)
            }
          }
        }
        // Post
        axios.post(scope.apiEndpoints[endpoint] + url, value, config)
          .then(function (res) {
            resolve(res.data)
          })
          .catch(function (err) {
            if (localUseOfflineMode === true && err != null && err.message === 'Network Error' && offlineCallbackFunction != null) {
              // Create the post object
              var postRequestObj = scope.createRequestObject('POST', url, value)
              localStoreObj.dispatch('offline/enqueueOfflineItem', { item: postRequestObj, state: localStoreObj.state.offline })
              resolve(localStoreObj.dispatch(offlineCallbackFunction, { url: url, value: value, state: localStoreObj.state }))
            } else {
              var errorMessage = ''
              var responseObj = err.response
              if (responseObj != null) {
                if (responseObj.data != null && Array.isArray(responseObj.data) && responseObj.data.length > 0) {
                  errorMessage += 'Save Error - ' + responseObj.data[0].description
                } else if (responseObj.data != null) {
                  errorMessage = responseObj.data.message || responseObj.data.Message
                }
              } else {
                errorMessage = err.message
              }
              reject(new Error(errorMessage))
            }
          })
      })
    } else {
      // Use the offline function and cache the request
      return new Promise(function (resolve, reject) {
        if (offlineCallbackFunction != null) {
          var postRequestObj = scope.createRequestObject('POST', url, value)
          localStoreObj.dispatch('offline/enqueueOfflineItem', { item: postRequestObj, state: localStoreObj.state.offline })
          resolve(localStoreObj.dispatch(offlineCallbackFunction, { url: url, value: value, state: localStoreObj.state }))
        } else {
          reject(new Error('System is offline and configured to handle offline requests but no handling is setup for this method'))
        }
      })
    }
  },

  /**
   * Main method to put values on the server
   *
   * @param {*} url
   * @param {*} value
   * @param {*} progressCallback
   * @param {*} offlineCallbackFunction
   */
  put (endpoint, url, value, progressCallback, offlineCallbackFunction) {
    // Local copies
    var scope = this
    var localStoreObj = store
    var localUseOfflineMode = this.useOfflineMode
    // If nothing is waiting to be sent this can be initiated immediately
    if (!localUseOfflineMode || localStoreObj.getters['offline/isOfflineQueueEmpty']) {
      // Try to make the request to the server
      return new Promise(function (resolve, reject) {
        // Build a config based on the parameters
        const config = {
          onUploadProgress: progressEvent => {
            if (progressCallback != null) {
              progressCallback(progressEvent)
            }
          }
        }
        // Try the put request to the server
        axios.put(scope.apiEndpoints[endpoint] + url, value, config)
          .then(function (res) {
            resolve(res.data)
          })
          .catch(function (err) {
            if (localUseOfflineMode === true && err != null && err.message === 'Network Error') {
              var putRequestObj = scope.createRequestObject('PUT', url, value)
              localStoreObj.dispatch('offline/enqueueOfflineItem', { item: putRequestObj, state: localStoreObj.state.offline })
              if (offlineCallbackFunction != null) {
                resolve(localStoreObj.dispatch(offlineCallbackFunction, { url: url, value: value, state: localStoreObj.state }))
              } else {
                resolve()
              }
            } else {
              var errorMessage = ''
              var responseObj = err.response
              if (responseObj != null) {
                if (responseObj.data != null && Array.isArray(responseObj.data) && responseObj.data.length > 0) {
                  errorMessage += 'Save Error - ' + responseObj.data[0].description
                } else if (responseObj.data != null) {
                  errorMessage = responseObj.data.message
                }
              } else {
                errorMessage = err.message
              }
              reject(new Error(errorMessage))
            }
          })
      })
    } else {
      // Use the offline function and cache the request
      return new Promise(function (resolve) {
        var putRequestObj = scope.createRequestObject('PUT', url, value)
        localStoreObj.dispatch('offline/enqueueOfflineItem', { item: putRequestObj, state: localStoreObj.state.offline })
        if (offlineCallbackFunction != null) {
          resolve(localStoreObj.dispatch(offlineCallbackFunction, { url: url, value: value, state: localStoreObj.state }))
        } else {
          resolve()
        }
      })
    }
  },

  /**
   * Main method to delete values from the server
   *
   * @param {*} url
   * @param {*} value
   */
  delete (endpoint, url, value) {
    const scope = this

    return new Promise(function (resolve, reject, cb) {
      const config = {
        onUploadProgress: progressEvent => {
          if (cb != null) {
            cb(progressEvent)
          }
        }
      }
      const fullpath = scope.apiEndpoints[endpoint] + url
      axios.delete(fullpath, {
        params: value,
        config: config
      })
        .then(function (res) {
          resolve(res.data)
        })
        .catch(function (err) {
          var errorMessage = ''
          var responseObj = err.response
          if (responseObj != null) {
            if (responseObj.data != null && Array.isArray(responseObj.data) && responseObj.data.length > 0) {
              errorMessage += 'Save Error - ' + responseObj.data[0].description
            } else if (responseObj.data != null) {
              errorMessage = responseObj.data.message
            }
          } else {
            errorMessage = err.message
          }
          reject(new Error(errorMessage))
        })
    })
  },
  /**
   * Helper method to create an offline request object
   *
   * @param {*} type
   * @param {*} url
   * @param {*} value
   */
  createRequestObject (type, url, value) {
    var requestObj = {
      id: GUID.generateUUID(),
      type: type,
      url: url,
      value: value,
      dateCreated: new Date()
    }
    return requestObj
  },
  /**
   * Helper method to handle a failed request attempt
   *
   * @param {*} err
   * @param {*} appObj
   */
  handleFailedOnlineRequestAttempt (err, appObj) {
    // If its a network error it just means the server is not online yet
    if (err != null && err.message === 'Network Error') {
      this.isPostingData = false
    } else {
      // If not its probably an error when trying to synchronize
      var errorMessage = 'Error Synchronising Data: '
      if (err != null && err.response != null) {
        if (err.response.data != null && err.response.data.length > 0) {
          errorMessage += err.response.data[0].description
        }
      }
      appObj.$snotify.error(errorMessage)
    }
    this.isPostingData = false
  }
}

axios.interceptors.request.use((config) => {
  const token = store.state.session.token

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  config.headers['Access-Control-Expose-Headers'] = 'Token-Expired'
  return config
}, (error) => {
  return Promise.reject(error)
})

axios.interceptors.response.use(response => {
  return response
}, error => {
  if (error.response != null && error.response.status === 401 && error.response.headers['token-expired']) {
    store.commit('session/setIsLoggedIn', false)
    store.commit('session/setSessionUser', {})
    store.commit('session/setPermissions', { routes: {}, keys: {} })
    store.commit('session/setToken', null)
    store.commit('session/setTokenExpired', true)
    // Keep the attempted route so a scan's site ID and query values survive re-login
    // Skip when already on /login (avoids nested redirects); concurrent 401s make
    // the router reject duplicate pushes, which is harmless, so swallow it
    if (router.currentRoute.path !== '/login') {
      router.push({ path: '/login', query: { redirect: router.currentRoute.fullPath } }).catch(() => {})
    }
  }
  return Promise.reject(error)
})
